// Owns the workspace "⋯" recent-views menu: the deduped, paginated list of every distinct view
// this session has landed on, and the single `record` entry point every caller uses to add one.
//
// Two write paths converge on ONE list:
//   • agent-driven views are recorded server-side by `publish_action` — the client only mirrors
//     them locally (optimistic, so the menu updates before the socket round-trip settles);
//   • user-driven views (a manual filter/facet/tab click, a `+` create, replaying an entry from
//     the menu) have no agent turn behind them, so those POST to the recent-views endpoint.
// Both derive the same `recentViewKey`, so the two paths collapse onto one row rather than
// producing a near-duplicate pair.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useLazyListRecentViewsQuery,
  useRecordRecentViewMutation,
  type RecentViewRow,
} from '@/redux/internal-services/agentApi';
import {
  recentViewKey,
  normalizeViewParams,
  upsertRecent,
  viewLabel,
  type RecentEntry,
  type RecentContextRef,
  type RecentViewSource,
} from '@/components/chat/recentViews';
import type { SamModule } from '@/components/SAM/samActionTypes';
import { socketService } from '@/services/SocketService';

/** Fallback when the caller hasn't resolved `chat_settings.recent_views_limit` yet (e.g. the
 *  config query is still in flight on first mount) — matches the backend's own default. */
const DEFAULT_LIMIT = 10;
/** Fallback for `chat_settings.recent_views_filter_merge_seconds` until the config loads. */
const DEFAULT_MERGE_SECONDS = 15;

export interface RecordViewInput {
  cardType: SamModule;
  cardDefinitionId?: string;
  title?: string;
  subtitle?: string;
  contextRef?: RecentContextRef | null;
  viewParams?: Record<string, unknown> | null;
  /** Defaults to 'agent'. Anything other than 'agent' is also POSTed to the server, because only
   *  the agent path is already persisted by `publish_action`. */
  source?: RecentViewSource;
  messageId?: string;
}

export interface RecentViews {
  /** Newest-picked first — the order the "⋯" menu renders. */
  entries: RecentEntry[];
  /** Whether an older page remains (drives the menu's scroll sentinel). */
  hasMore: boolean;
  loadMore: () => void;
  /** Add (or bump) a view. Safe to call on every render path — identical views collapse. */
  record: (input: RecordViewInput) => void;
  /** Drop everything and re-read from the server (called when the session changes). */
  reset: () => void;
}

function toEntry(row: RecentViewRow): RecentEntry {
  const contextRef = row.context_ref && row.context_ref.type ? row.context_ref : undefined;
  const viewParams = normalizeViewParams(row.view_params);
  return {
    // Recomputed rather than taken from `entry_key`: the server stores a sha1, the client keys on
    // the canonical string. Same inputs → the two always agree on identity.
    key: recentViewKey({ cardType: row.card_type, contextRef, viewParams }),
    cardType: row.card_type as SamModule,
    cardDefinitionId: row.card_definition_id || '',
    title: row.title || '',
    subtitle: row.subtitle || undefined,
    contextRef: contextRef || undefined,
    viewParams,
    source: (row.source || 'agent') as RecentViewSource,
    messageId: row.message_id || undefined,
    lastAccessedAt: row.last_accessed_at_ms
      ?? (row.last_accessed_at ? new Date(row.last_accessed_at).getTime() : Date.now()),
  };
}

export function useRecentViews(
  conversationId: string,
  limit: number = DEFAULT_LIMIT,
  opts: { mergeSeconds?: number; currentUserId?: string } = {},
): RecentViews {
  const mergeMs = Math.max(0, opts.mergeSeconds ?? DEFAULT_MERGE_SECONDS) * 1000;
  const currentUserId = opts.currentUserId;
  // Never below 1 — a misconfigured/zero value must not silently hide the menu entirely.
  const cap = Math.max(1, limit || DEFAULT_LIMIT);
  const [entries, setEntries] = useState<RecentEntry[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [fetchPage] = useLazyListRecentViewsQuery();
  const [postView] = useRecordRecentViewMutation();
  // How many rows have been pulled so far — the next page's `skip`.
  const loadedRef = useRef(0);
  // Which conversation a page fetch is currently in flight FOR (null = none). Conversation-scoped
  // rather than a bare boolean: a fetch still running for a PREVIOUS conversation must not suppress
  // the new one. It used to — switching sessions mid-fetch made the [conversationId] effect's own
  // loadPage(0) bail on the shared flag, then the old response hit the staleness guard below and
  // returned, so nothing ever fetched the new session's history. `loadMore` couldn't rescue it
  // either, since reset() sets hasMore=false and that gates it.
  const loadingForRef = useRef<string | null>(null);
  const conversationRef = useRef(conversationId);
  conversationRef.current = conversationId;
  // Mirrors `entries` so `record` can merge against the current list without taking it as a dep —
  // ChatPage passes `record` straight into other hooks, so its identity must stay stable.
  const entriesRef = useRef<RecentEntry[]>(entries);
  entriesRef.current = entries;
  // The last filter click made in THIS tab. The merge window is measured against it rather than
  // the top entry's `lastAccessedAt`, which may be a server timestamp — client/server clock skew
  // made that difference negative, so an old persisted row could be superseded (and deleted).
  const lastFilterClickRef = useRef<{ key: string; at: number } | null>(null);

  const loadPage = useCallback(async (skip: number) => {
    const id = conversationRef.current;
    if (!id) return;
    if (skip >= cap) return; // already at the configured ceiling — nothing more to show
    if (loadingForRef.current === id) return; // a page for THIS conversation is already in flight
    loadingForRef.current = id;
    try {
      // Never request past the ceiling, even if a page-sized fetch would overshoot it.
      const pageLimit = Math.min(cap - skip, cap);
      const { rows, total } = await fetchPage({ conversationId: id, skip, limit: pageLimit }).unwrap();
      // The session can change while a page is in flight — drop a response for the old one.
      if (conversationRef.current !== id) return;
      const page = rows.map(toEntry);
      setEntries((prev) => {
        // Merge rather than concat: an optimistic local entry for a row in this page must not
        // appear twice, and the server's copy (with its real timestamp) wins.
        const byKey = new Map(prev.map((e) => [e.key, e]));
        for (const e of page) byKey.set(e.key, e);
        // Trimmed to `cap` here too, not just below on the `record()` path — a page fetched right
        // after the limit was lowered (or after other entries were recorded locally) could
        // otherwise leave more than `cap` rows in state until the next reload.
        return [...byKey.values()].sort((a, b) => b.lastAccessedAt - a.lastAccessedAt).slice(0, cap);
      });
      loadedRef.current = skip + rows.length;
      setHasMore(loadedRef.current < total && loadedRef.current < cap);
    } catch {
      // A brand-new session has no recent views yet (404 until the first turn creates it) —
      // an empty menu is the correct outcome, not an error to surface.
      if (conversationRef.current === id) setHasMore(false);
    } finally {
      // Release only if a newer conversation's load hasn't already claimed the slot.
      if (loadingForRef.current === id) loadingForRef.current = null;
    }
  }, [fetchPage, cap]);

  const reset = useCallback(() => {
    setEntries([]);
    setHasMore(false);
    loadedRef.current = 0;
  }, []);

  // A filter-click view's AI-written title + description (chat_settings.recent_views_ai_labels),
  // generated in the background after the POST. The server only sends it once the row really
  // took it — a view SAM titled meanwhile keeps SAM's title — so it is safe to apply as-is.
  useEffect(() => {
    const onLabelled = (raw: unknown) => {
      const ev = raw as { session_id?: string; card_type?: string; view_params?: Record<string, unknown>; title?: string; subtitle?: string };
      if (!ev?.title || !ev.card_type || ev.session_id !== conversationRef.current) return;
      const key = recentViewKey({ cardType: ev.card_type, viewParams: ev.view_params });
      setEntries((prev) => prev.map((e) => (e.key === key
        ? { ...e, title: ev.title || e.title, subtitle: ev.subtitle || e.subtitle } : e)));
    };
    socketService.on('recent_view:labelled', onLabelled);
    return () => { socketService.off('recent_view:labelled', onLabelled); };
  }, []);

  // Load the first page whenever the open session changes, or the configured cap does (e.g. the
  // chat-config query resolves a beat after mount with a value other than DEFAULT_LIMIT).
  useEffect(() => {
    reset();
    if (conversationId) loadPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, cap]);

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    loadPage(loadedRef.current);
  }, [hasMore, loadPage]);

  const record = useCallback((input: RecordViewInput) => {
    const source = input.source ?? 'agent';
    const contextRef = input.contextRef && input.contextRef.type ? input.contextRef : undefined;
    const viewParams = normalizeViewParams(input.viewParams);
    const key = recentViewKey({ cardType: input.cardType, contextRef, viewParams });
    // Carry over anything this call doesn't supply from the entry already on screen — a manual
    // filter change on an agent-titled card sends no title/caption/card id, and the menu should never
    // regress to a blank heading. Resolved ONCE here, outside the state updater, so the optimistic
    // row and the POST below agree: the POST used to send the raw (empty) input while only the
    // local row got the merge, which blanked the persisted card id on every manual filter tweak.
    const existing = entriesRef.current.find((e) => e.key === key);
    // Real title (what the caller explicitly provided, or an existing agent-authored title on this
    // key). Sent to the backend as-is — empty means "no real title": the menu shows viewLabel()
    // until the server's AI label arrives over the socket (`recent_view:labelled`, below).
    const apiTitle = input.title || existing?.title || '';
    // Display title — shown immediately in the menu. Falls back to a deterministic module+filter
    // label (e.g. "My Open Tasks") so a user-driven view is never blank.
    const displayTitle = apiTitle || (source !== 'agent' ? viewLabel(input.cardType, viewParams, currentUserId) : '');
    // Rapid filter clicks on the same list collapse into ONE entry (latest filter wins): the newest
    // entry is replaced when it is also a user-filter view (AI-titled or not) of this list, recorded within
    // the merge window. Agent views and record-bound views are never replaced.
    const top = entriesRef.current[0];
    const last = lastFilterClickRef.current;
    const superseded = source === 'user_filter' && mergeMs > 0 && !contextRef && !input.title
      && top && top.key !== key && last?.key === top.key && top.source === 'user_filter'
      && top.cardType === input.cardType && !top.contextRef && Date.now() - last.at <= mergeMs
      ? top : undefined;
    const entry: RecentEntry = {
      key,
      cardType: input.cardType,
      cardDefinitionId: input.cardDefinitionId || existing?.cardDefinitionId || '',
      title: displayTitle,
      subtitle: input.subtitle || existing?.subtitle,
      contextRef,
      viewParams,
      source,
      messageId: input.messageId || existing?.messageId,
      lastAccessedAt: Date.now(),
    };
    // Optimistic: the menu reflects the view the moment it opens, on either path. `upsertRecent`
    // always puts the touched entry first, so trimming to `cap` here only ever drops whichever
    // entry is now the OLDEST — exactly "only show the latest N".
    setEntries((prev) => upsertRecent(superseded ? prev.filter((e) => e.key !== superseded.key) : prev, entry).slice(0, cap));
    if (source === 'user_filter') lastFilterClickRef.current = { key, at: Date.now() };
    if (source === 'agent') return; // already persisted server-side by publish_action
    const id = conversationRef.current;
    if (!id) return;
    postView({
      conversationId: id,
      card_type: entry.cardType,
      card_definition_id: entry.cardDefinitionId,
      title: apiTitle,   // NOT displayTitle — empty lets the server write an AI label for this row
      subtitle: entry.subtitle,
      context_ref: entry.contextRef ?? null,
      view_params: entry.viewParams,
      source,
      message_id: entry.messageId ?? null,
      replaces: superseded
        ? { card_type: superseded.cardType, context_ref: null, view_params: superseded.viewParams }
        : null,
    }).catch(() => {
      // The optimistic entry stays on screen for this session; it just won't survive a reload.
    });
  }, [postView, cap, mergeMs, currentUserId]);

  return { entries, hasMore, loadMore, record, reset };
}
